// Shell-level API helpers: alerts, events (calendar), notifications, profile,
// company (portal users), impersonation and server language.

import { adminApi, legacyAuthApi, toFormData } from '../../api/client';
import type { VendiaRuntimeUser } from '../../runtime';

/** `GET me` returns more than the runtime type declares. */
export type ShellUser = VendiaRuntimeUser & {
  avatar?: string | null;
  locale?: string | null;
  location_id?: string | null;
  customer_id?: string | null;
  supplier_id?: string | null;
  merchant_id?: string | null;
  can_impersonate?: number | boolean;
  locations?: Array<{ id: string; name: string }>;
  settings?: Record<string, unknown> | unknown[];
  acting_user?: { name?: string; username?: string; avatar?: string | null; location_id?: string | null } | null;
};

// ---------------------------------------------------------------------------
// Alerts (AlertController@index)
// ---------------------------------------------------------------------------
export type AlertCounts = {
  expenses: number;
  payments: number;
  customers: number;
  suppliers: number;
  expiry_alert: number;
  quantity_alert: number;
  recurring_soon: number;
  payment_reviews: number;
  notifications: number;
};

export async function fetchAlerts(): Promise<AlertCounts> {
  const response = await adminApi.get<AlertCounts>('alerts');
  return response.data;
}

// ---------------------------------------------------------------------------
// Events / calendar (EventController)
// ---------------------------------------------------------------------------
export type CalendarEvent = {
  id: string;
  title: string;
  details?: string | null;
  start_date: string;
  end_date?: string | null;
  start_time?: string | null;
  end_time?: string | null;
  color?: string | null;
  user_id?: string;
};

export type CalendarEventInput = Omit<CalendarEvent, 'id' | 'user_id'>;

/** `GET events?month=YYYY-MM` → `{ "YYYY-MM-DD": Event[] }` (grouped by start_date). */
export async function fetchMonthEvents(month: string): Promise<Record<string, CalendarEvent[]>> {
  const response = await adminApi.get('events', { params: { month } });
  const data = response.data;
  return data && typeof data === 'object' && !Array.isArray(data) ? (data as Record<string, CalendarEvent[]>) : {};
}

export async function saveEvent(input: CalendarEventInput, id?: string) {
  const response = id ? await adminApi.put(`events/${id}`, input) : await adminApi.post('events', input);
  return response.data as { success: boolean; data: CalendarEvent };
}

export async function deleteEvent(id: string) {
  const response = await adminApi.delete(`events/${id}`);
  return response.data as { success: boolean };
}

// ---------------------------------------------------------------------------
// Notifications (NotificationController) — Laravel paginator, 20 per page
// ---------------------------------------------------------------------------
export type AppNotification = {
  id: string;
  type: string;
  data: { title?: string; description?: string; [key: string]: unknown };
  read_at: string | null;
  created_at: string;
};

export type Paginator<T> = {
  current_page: number;
  data: T[];
  from: number | null;
  to: number | null;
  last_page: number;
  per_page: number;
  total: number;
};

export async function fetchNotifications(page: number, unreadOnly: boolean) {
  const response = await adminApi.get<Paginator<AppNotification>>('notifications', {
    params: { page, ...(unreadOnly ? { unread: 'yes' } : {}) }
  });
  return response.data;
}

export const notificationActions = {
  markRead: (id: string) => adminApi.put(`notifications/${id}`),
  markUnread: (id: string) => adminApi.post(`notifications/${id}`),
  remove: (id: string) => adminApi.delete(`notifications/${id}`),
  readAll: () => adminApi.post('notifications/read/all'),
  deleteAll: () => adminApi.post('notifications/delete/all')
};

// ---------------------------------------------------------------------------
// Profile (ProfileController)
// ---------------------------------------------------------------------------
export type Profile = {
  name: string;
  phone: string | null;
  email: string;
  avatar: string | null;
  username: string;
  settings: Record<string, unknown>;
};

export async function fetchProfile(): Promise<Profile> {
  const response = await adminApi.get('profile');
  const data = response.data ?? {};
  return { ...data, settings: data.settings && !Array.isArray(data.settings) ? data.settings : {} };
}

/** `POST profile` (multipart when an avatar File is attached). */
export async function saveProfile(payload: { name: string; phone: string; settings: Record<string, unknown>; avatar?: File | null }) {
  const { avatar, ...rest } = payload;
  const body = avatar ? toFormData({ ...rest, avatar }) : rest;
  const response = await adminApi.post('profile', body);
  return response.data as Profile & { success?: boolean };
}

export async function changePassword(payload: { current: string; password: string; password_confirmation: string }) {
  const response = await adminApi.post('profile/change_password', payload);
  return response.data as { success: boolean };
}

// ---------------------------------------------------------------------------
// Company (UserCompanyController) — customer / supplier portal users
// ---------------------------------------------------------------------------
export async function fetchCompany(): Promise<Record<string, unknown>> {
  const response = await adminApi.get('user/company');
  return response.data && !Array.isArray(response.data) ? response.data : {};
}

export async function saveCompany(payload: Record<string, unknown>) {
  const response = await adminApi.post('user/company', payload);
  return response.data as { success: boolean };
}

export async function fetchCountries(): Promise<Array<{ value: string; label: string }>> {
  const response = await adminApi.get('countries');
  const data = response.data;
  const list = Array.isArray(data) ? data : Object.values(data ?? {});
  return (list as Array<Record<string, unknown>>).map((row) => ({ value: String(row.value ?? row.isoCode ?? ''), label: String(row.label ?? row.name ?? '') }));
}

export async function fetchStates(country: string): Promise<Array<{ value: string; label: string }>> {
  if (!country) return [];
  const response = await adminApi.get('states', { params: { country } });
  const data = response.data;
  const list = Array.isArray(data) ? data : Object.values(data ?? {});
  return (list as Array<{ value: string; label: string }>).filter((row) => row && row.value);
}

// ---------------------------------------------------------------------------
// Impersonation (AjaxController@impersonateUrl, UserController@impersonate/stop)
// ---------------------------------------------------------------------------
/**
 * Start impersonating `username`. The backend requires a signed URL, so fetch it
 * first (`GET impersonate/{username}/url`) and POST to it. Used by the users
 * row action; resolves to the acting user on success.
 */
export async function startImpersonation(username: string) {
  const urlResponse = await adminApi.get<{ success: boolean; url: string }>(`impersonate/${encodeURIComponent(username)}/url`);
  if (!urlResponse.data?.success || !urlResponse.data.url) return { success: false as const };
  const signed = new URL(urlResponse.data.url, window.location.origin);
  // Re-root the signed URL on the current origin (the dev proxy forwards /admin).
  const response = await adminApi.post(`${signed.pathname.replace(/^.*\/admin\/app\//, '')}${signed.search}`);
  return response.data as { success: boolean; user?: { name: string; username: string } };
}

export async function stopImpersonation() {
  const response = await adminApi.post('impersonate/stop');
  return response.data as { success: boolean };
}

// ---------------------------------------------------------------------------
// Server language (AjaxController@locale) — only locales with an MPS lang file
// are accepted (400 otherwise), so failures are ignored.
// ---------------------------------------------------------------------------
export async function syncServerLanguage(locale: string) {
  try {
    await adminApi.get(`language/${locale}`);
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Password reset (modules/Auth) — guest endpoints at the site root
// ---------------------------------------------------------------------------
export async function requestPasswordReset(email: string) {
  const response = await legacyAuthApi.post('password/email', { email });
  return response.data as { success: boolean; message?: string };
}

export async function resetPassword(payload: { token: string; email: string; password: string; password_confirmation: string }) {
  const response = await legacyAuthApi.post('password/reset', payload);
  return response.data as { success: boolean; message?: string };
}
